import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AccountPicker,
  AssetCard,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Copyable,
  CurrencyAmountUI,
  CurrencyFlagEmoji,
  cn,
  DateUI,
  entryFromEventFragment,
  entryFromFragment,
  Icon,
  PrimaryFullScreenSpinner,
  StatusUI,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TimeLogPersonActor,
  Txt,
  toast,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import type React from "react";
import { Suspense } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import { RiRobot2Line } from "react-icons/ri";
import { AdminCurrencycloudBeneficiaryCard } from "~/components/Currencycloud/AdminCurrencycloudBeneficiaryCard";
import { AdminCurrencycloudPaymentSection } from "~/components/Currencycloud/AdminCurrencycloudPaymentSection";
import { DetailRow, DetailSubRow } from "~/components/DetailRow/DetailRow";
import { AdminEburyBeneficiaryCard } from "~/components/Ebury/AdminEburyBeneficiaryCard";
import { AdminEburyPaymentSection } from "~/components/Ebury/AdminEburyPaymentSection";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { PaneActions } from "~/components/operations/actions";
import { EditPayoutAmountDialog } from "~/components/Payout/EditPayoutAmountDialog";
import { PayoutDateDialog } from "~/components/Payout/PayoutDateDialog";
import { RelatedLedgerTransactions } from "~/components/RelatedLedgerTransactions";
import { TransactionMonitoringFindingCard } from "~/components/TransactionMonitoring/TransactionMonitoringFindingCard";
import { TransferGroupUI } from "~/components/TransferGroup/TransferGroupUI";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

type TimeLogEntry = ReturnType<typeof entryFromFragment>;

function TimeLogSystemActor({
  actor,
  prefix,
}: {
  actor: { kind: "SYSTEM"; id: string; name: string };
  prefix?: React.ReactNode;
}) {
  return (
    <>
      {prefix ? <span className="whitespace-nowrap">{prefix}</span> : null}
      <span className="-my-1 inline-flex items-center gap-1 rounded-full border bg-white p-1 pr-2 align-middle text-xs shadow-surface">
        <div className="flex items-center justify-center pl-1">
          <RiRobot2Line />
        </div>
        <div className="max-w-[30ch] truncate">
          {actor.name} {actor.id}
        </div>
      </span>
    </>
  );
}

const TimeLogEntryCompact = ({ at, description, by }: TimeLogEntry) => {
  return (
    <div className="inline-grid place-content-start gap-1">
      <Txt variant="caption" tone="secondary" nowrap>
        {DateTime.fromJSDate(at).toLocaleString(DateTime.DATETIME_MED)}
      </Txt>
      <Txt variant="body">
        {description}
        &nbsp;
        {by && by.kind === "PERSON" ? (
          <TimeLogPersonActor prefix="by " actor={by} />
        ) : null}
        {by && by.kind === "SYSTEM" ? (
          <TimeLogSystemActor prefix="by " actor={by} />
        ) : null}
      </Txt>
    </div>
  );
};

const TimeLogCompact = ({
  entries,
  className,
}: {
  entries: TimeLogEntry[];
  className?: string;
}) => {
  const stortedEntriesDateDesc = entries.sort(
    (a, z) => z.at.getTime() - a.at.getTime(),
  );

  return (
    <div className={cn(className, "grid place-content-start gap-4")}>
      {stortedEntriesDateDesc.map((entry, i) => (
        <TimeLogEntryCompact key={entry.at.toISOString() + i} {...entry} />
      ))}
    </div>
  );
};

export const Route = createFileRoute("/__auth/payouts_/$payoutId")({
  head: () => opsHead("Payout"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { payoutId } = Route.useParams();

  const { data, error } = useSuspenseQuery(
    graphql(
      `
        query AdminPayoutPage($payoutId: ID!) {
          payout: node(id: $payoutId) {
            id
            ... on Payout {
              shortRef
              status
              canUpdateSourceAsset
              ledgerTransactions {
                id
                ...RelatedLedgerTransactions
              }
              requestedForDate
              amount {
                amount
                currency
              }
              ...EditPayoutAmountDialogFields
              approvalRequest {
                id
                approvalPolicy {
                  id
                  numUserReviewsRequired
                }
                currentApprovedCount
                result
              }
              paymentReference
              sourceAsset {
                id
                class
                businessEntity {
                  id
                }
                ... on FXAsset {
                  id
                  provider {
                    internalName
                  }
                }
              }
              beneficiaryAccount {
                id
                ...BeneficiaryAssetCardFields
                ... @defer {
                  eburyBeneficiaries {
                    id
                    businessEntity {
                      id
                    }
                    ...AdminEburyBeneficiaryCard
                  }
                }
                ... @defer {
                  currencycloudBeneficiaries {
                    id
                    businessEntity {
                      id
                    }
                    ...AdminCurrencycloudBeneficiaryCard
                  }
                }
              }
              organisation {
                id
                name
                squareLogoUrl
                assets {
                  id
                  currency
                  ... AssetPickerFields
                }
              }
              executionMethod
              expectedProcessingDate
              updates {
                kind
                ...TimeLogEntry
              }
              events {
                ...TimeLogEventEntry
              }
              ...OperationsPayoutActions
              eburyPayment {
                id
                ...AdminEburyPaymentSection
              }
              currencycloudPayment {
                id
                ...AdminCurrencycloudPaymentSection
              }
              transactionMonitoringDetails {
                status
                blockingFindings {
                  id
                  ...TransactionMonitoringFindingCardFields
                }
              }
              screeningEntity {
                ... on BusinessEntity {
                  id
                  name
                  latestValidScreeningResult {
                    id
                    status
                    url
                    createdAt
                  }
                }
                ... on BeneficiaryAccount {
                  id
                  accountName
                  latestValidScreeningResult {
                    id
                    status
                    url
                    createdAt
                  }
                }
              }
              transferGroup {
                  id
                  kind
                  ...AdminTransferGroup
              }
              fxTrade {
                id
                externalId
                amount {
                  amount
                  currency
                }
                buyCurrency
                sellCurrency
                status
                operation
              }
            }
          }
        }
      `,
    ),
    {
      variables: {
        payoutId,
      },
    },
  );

  const payout = data?.payout;
  if (!payout || payout.__typename !== "Payout") {
    throw new NotFoundError();
  }

  useOpsTitle(`Payout #${payout.shortRef}`);

  const { updates, executionMethod, approvalRequest } = payout;

  const beneficiaryAccount = payout.beneficiaryAccount;
  if (!beneficiaryAccount) {
    throw new Error("expected beneficiary account");
  }

  const organisation = payout.organisation;
  if (!organisation) {
    throw new Error("expected organisation on payout");
  }

  const sourceAsset = payout.sourceAsset;

  const sourceAssetOptions =
    organisation.assets?.filter(
      (asset) =>
        asset.class === "FX" || asset.currency === payout.amount.currency,
    ) ?? [];

  const creationEvent = updates?.find(
    (update) => update.kind === "created" && update.by,
  );

  const [updateSourceAsset] = useMutation(
    graphql(`
      mutation UpdateSourceAsset($payoutId: ID!, $sourceAssetId: ID!) {
          updatePayout(input: {
             id: $payoutId,
             sourceAssetId: $sourceAssetId
          }) {
              payout {
                  id
                  sourceAsset {
                      id
                  }
                  executionMethod
                  ...OperationsPayoutActions
                  events {
                    ...TimeLogEventEntry
                  }
              }
          }
      }
  `),
    {
      onError() {
        toast({
          title: "Something went wrong",
          description: "Please refresh the page and try again",
          kind: "error",
          autoHide: false,
        });
      },
    },
  );

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`Payout #${payout.shortRef}`}
        subtitle={<div className="font-mono">{payout.id}</div>}
      />
      <Tabs defaultValue="details">
        <TabsList size="sm" className="mb-6">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <div className="flex flex-col gap-6">
            <div className="flex flex-row justify-between gap-6">
              <div className="flex w-2/3 flex-col">
                <Card>
                  <CardHeader>
                    <CardTitle>Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {payout.status && (
                      <DetailRow
                        label="Status"
                        value={<StatusUI status={payout.status} />}
                      />
                    )}
                    {creationEvent ? (
                      <DetailRow
                        label="Created by"
                        value={
                          <TimeLogPersonActor
                            actor={{
                              ...creationEvent.by,
                              kind: "PERSON",
                              avatarUrl:
                                creationEvent?.by?.avatarUrl ?? undefined,
                            }}
                          />
                        }
                      />
                    ) : null}
                    <DetailRow
                      label="Client"
                      value={
                        <div className="flex flex-row items-center gap-2">
                          <Icon
                            size="8"
                            imageUrl={organisation.squareLogoUrl ?? undefined}
                            fallbackIcon={<PiBuildingOffice />}
                          />
                          <Link to={`/organisations/${organisation?.id}`}>
                            <div className="max-w-[7.5rem] hover:underline">
                              <Txt variant="name" truncate>
                                {organisation.name}
                              </Txt>
                            </div>
                          </Link>
                        </div>
                      }
                    />
                    <DetailRow
                      label="Amount"
                      value={<EditPayoutAmountDialog payout={payout} />}
                    />
                    <DetailRow
                      label="Date"
                      value={<PayoutDateDialog payout={payout} />}
                    />
                    <DetailRow
                      label="Expected processing date"
                      value={
                        payout.expectedProcessingDate ? (
                          <DateUI
                            date={payout.expectedProcessingDate}
                            variant="DATE_TZ"
                          />
                        ) : (
                          "-"
                        )
                      }
                    />
                    <DetailRow
                      label="Source account"
                      value={
                        <AccountPicker
                          accounts={sourceAssetOptions}
                          value={sourceAsset?.id}
                          onChange={(id) => {
                            const newSourceAsset = sourceAssetOptions.find(
                              (a) => a.id === id,
                            );
                            if (!newSourceAsset) {
                              throw new Error("expected source asset");
                            }
                            updateSourceAsset({
                              variables: {
                                payoutId,
                                sourceAssetId: id,
                              },
                              optimisticResponse: {
                                __typename: "Mutation",
                                updatePayout: {
                                  __typename: "UpdatePayoutPayload",
                                  payout: {
                                    __typename: "Payout",
                                    id: payoutId,
                                    sourceAsset: {
                                      __typename: newSourceAsset.__typename,
                                      id,
                                    },
                                    executionMethod: "MANUAL",
                                  },
                                },
                              },
                            });
                          }}
                          disabled={!payout.canUpdateSourceAsset}
                          showAvailableBalance
                          defaultGroupBy="ENTITY"
                          allSelectable
                        />
                      }
                    />
                    <DetailRow
                      label="To Account"
                      value={
                        <div className="flex items-center gap-4">
                          <Link
                            to={`/beneficiary-accounts/${beneficiaryAccount.id}`}
                          >
                            <div className="flex w-fit cursor-pointer flex-col gap-4 rounded-sm p-2 hover:bg-secondary">
                              <AssetCard
                                beneficiaryAccount={beneficiaryAccount}
                                subText={[
                                  "IDENTIFIER",
                                  "CURRENCY",
                                  "BUSINESS_ENTITY",
                                ]}
                              />
                            </div>
                          </Link>

                          {executionMethod === "EBURY_PAYMENT" &&
                            sourceAsset?.businessEntity && (
                              <AdminEburyBeneficiaryCard
                                eburyBeneficiary={
                                  beneficiaryAccount.eburyBeneficiaries?.find(
                                    (b) =>
                                      b.businessEntity?.id ===
                                      sourceAsset.businessEntity?.id,
                                  ) ?? null
                                }
                                eburyBusinessEntityId={
                                  sourceAsset.businessEntity?.id
                                }
                                beneficiaryAccountId={beneficiaryAccount.id}
                                errors={error?.graphQLErrors?.filter(
                                  (gqlError) => {
                                    return (
                                      gqlError.path?.at(0) === "payout" &&
                                      gqlError.path?.at(1) ===
                                        "beneficiaryAccount" &&
                                      gqlError.path?.at(2) ===
                                        "eburyBeneficiaries"
                                    );
                                  },
                                )}
                              />
                            )}

                          {executionMethod === "CURRENCYCLOUD_PAYMENT" &&
                            sourceAsset?.businessEntity && (
                              <AdminCurrencycloudBeneficiaryCard
                                currencycloudBeneficiary={
                                  beneficiaryAccount.currencycloudBeneficiaries ===
                                  undefined
                                    ? undefined
                                    : (beneficiaryAccount.currencycloudBeneficiaries?.find(
                                        (b) =>
                                          b.businessEntity?.id ===
                                          sourceAsset?.businessEntity?.id,
                                      ) ?? null)
                                }
                                currencycloudBusinessEntityId={
                                  sourceAsset.businessEntity.id
                                }
                                beneficiaryAccountId={beneficiaryAccount.id}
                                errors={error?.graphQLErrors?.filter(
                                  (gqlError) => {
                                    return (
                                      gqlError.path?.at(0) === "payout" &&
                                      gqlError.path?.at(1) ===
                                        "beneficiaryAccount" &&
                                      gqlError.path?.at(2) ===
                                        "currencycloudBeneficiaries"
                                    );
                                  },
                                )}
                              />
                            )}
                        </div>
                      }
                    />
                    <DetailRow
                      label="Payment reference"
                      value={
                        <div className="flex items-center gap-6">
                          <Copyable
                            tooltipSide="right"
                            className="bg-secondary font-mono text-primary"
                          >
                            {payout.paymentReference}
                          </Copyable>

                          <span className="text-tertiary text-xs italic">
                            Include in payment instructions for automatic
                            reconcilliation
                          </span>
                        </div>
                      }
                    />

                    {payout.fxTrade && (
                      <DetailRow
                        label="FX trade"
                        value={
                          <Link to={`/fx-trades/${payout.fxTrade.id}`}>
                            <div className="flex flex-col gap-2">
                              <div className="flex gap-2">
                                <div className="flex items-center gap-2">
                                  Sell {payout.fxTrade.sellCurrency}
                                  <CurrencyFlagEmoji
                                    currency={payout.fxTrade.sellCurrency}
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  Buy {payout.fxTrade.buyCurrency}
                                  <CurrencyFlagEmoji
                                    currency={payout.fxTrade.buyCurrency}
                                  />
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <CurrencyAmountUI
                                  amount={payout.fxTrade.amount.amount}
                                  currency={payout.fxTrade.amount.currency}
                                />

                                {payout.fxTrade.__typename === "EburyFXTrade" &&
                                  payout.fxTrade.externalId && (
                                    <div className="text-tertiary">
                                      Ebury Trade ID:{" "}
                                      {payout.fxTrade.externalId}
                                    </div>
                                  )}

                                {payout.fxTrade.__typename ===
                                  "CurrencycloudFXTrade" &&
                                  payout.fxTrade.externalId && (
                                    <div className="text-tertiary">
                                      Visa Trade ID: {payout.fxTrade.externalId}
                                    </div>
                                  )}

                                <StatusUI status={payout.fxTrade.status} />
                              </div>
                            </div>
                          </Link>
                        }
                      />
                    )}

                    <AdminEburyPaymentSection
                      enableDownloadReceiptButton={
                        payout.status === "COMPLETED"
                      }
                      businessEntityId={
                        payout.sourceAsset?.businessEntity?.id ?? ""
                      }
                      eburyPayment={payout.eburyPayment}
                      errors={error?.graphQLErrors?.filter((gqlError) => {
                        return (
                          gqlError.path?.at(0) === "payout" &&
                          gqlError.path?.at(1) === "eburyPayment"
                        );
                      })}
                    />

                    <AdminCurrencycloudPaymentSection
                      currencycloudPayment={payout.currencycloudPayment}
                      errors={error?.graphQLErrors?.filter((gqlError) => {
                        return (
                          gqlError.path?.at(0) === "payout" &&
                          gqlError.path?.at(1) === "currencycloudPayment"
                        );
                      })}
                    />

                    {approvalRequest && (
                      <DetailRow
                        label="Approval"
                        value={
                          <>
                            <DetailSubRow
                              label={"Status"}
                              value={
                                <StatusUI status={approvalRequest.result} />
                              }
                            />
                            <DetailSubRow
                              label={"# required"}
                              value={
                                approvalRequest.approvalPolicy
                                  ?.numUserReviewsRequired ?? "Unknown"
                              }
                            />
                            <DetailSubRow
                              label={"# approved"}
                              value={approvalRequest.currentApprovedCount}
                            />
                          </>
                        }
                      />
                    )}

                    {payout.transactionMonitoringDetails && (
                      <DetailRow
                        label="Transaction Monitoring"
                        value={
                          <div className="flex flex-col gap-2">
                            {payout.transactionMonitoringDetails.status && (
                              <div>
                                <StatusUI
                                  status={
                                    payout.transactionMonitoringDetails.status
                                  }
                                />
                              </div>
                            )}
                            {payout.transactionMonitoringDetails
                              .blockingFindings && (
                              <div className="flex flex-col gap-1">
                                {payout.transactionMonitoringDetails.blockingFindings.map(
                                  (finding) => (
                                    <TransactionMonitoringFindingCard
                                      key={finding.id}
                                      finding={finding}
                                    />
                                  ),
                                )}
                              </div>
                            )}
                          </div>
                        }
                      />
                    )}

                    {payout.screeningEntity && (
                      <DetailRow
                        label="Entity Screening"
                        value={
                          <div className="flex flex-col gap-2 text-xs">
                            <div>
                              The entity "
                              {payout.screeningEntity.__typename ===
                              "BusinessEntity"
                                ? payout.screeningEntity.name
                                : payout.screeningEntity.accountName}
                              " is required to be screened before this payout
                              can continue.
                            </div>
                            <div>
                              Screening status{" "}
                              {payout.screeningEntity.latestValidScreeningResult
                                ?.status ?? "Unknown"}
                              {payout.screeningEntity
                                .latestValidScreeningResult && (
                                <Link
                                  to={
                                    payout.screeningEntity
                                      .latestValidScreeningResult.url
                                  }
                                  className="ml-1 underline"
                                >
                                  View screening in BNDRY
                                </Link>
                              )}
                            </div>
                          </div>
                        }
                      />
                    )}

                    {updates ? (
                      <DetailRow
                        label="Updates"
                        value={
                          <TimeLogCompact
                            entries={updates.map(entryFromFragment)}
                          />
                        }
                      />
                    ) : null}

                    {payout.events ? (
                      <DetailRow
                        label="Event log"
                        value={
                          <TimeLogCompact
                            entries={payout.events.map(entryFromEventFragment)}
                          />
                        }
                      />
                    ) : null}
                  </CardContent>
                </Card>
              </div>

              <div className="flex flex-col gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PaneActions {...payout} />
                  </CardContent>
                </Card>
              </div>
            </div>
            {payout.transferGroup && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    Transfer group ({prettyPrintEnum(payout.transferGroup.kind)}
                    )
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-4">
                    <TransferGroupUI
                      transferGroup={payout.transferGroup}
                      currentFundMovementId={null}
                    />

                    <Link to={`/transfer-groups/${payout.transferGroup.id}`}>
                      <Button variant="secondary">View transfer group</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="transactions">
          <RelatedLedgerTransactions
            transactions={payout.ledgerTransactions ?? []}
          />
        </TabsContent>
      </Tabs>
    </AdminPageContainer>
  );
}
